import { QUIZ_BANK_SKELETON_COUNT } from '../consts';
import { type QuizBankProblem, type QuizBankStandard } from '../types';
import { QuizBankEmptyResult } from './QuizBankEmptyResult';
import { QuizBankErrorState } from './QuizBankErrorState';
import { QuizBankProblemCard } from './QuizBankProblemCard';
import { QuizBankProblemCardSkeleton } from './QuizBankProblemCardSkeleton';
interface QuizBankProblemListProps {
  status: 'loading' | 'success' | 'error';
  problems: QuizBankProblem[];
  selectedIds: string[];
  addedIds: string[];
  /** 선택 상한에 닿았다 — 미선택 카드는 더 고를 수 없다 */
  selectionFull?: boolean;
  /** 0건일 때 되비출 검색어 */
  query: string;
  /** 단원명에 칠할 hit 자리(서버 `matchedUnits`). 본문 hit 는 문항이 스스로 든다 */
  unitHitTermsById?: ReadonlyMap<string, readonly string[]>;
  /** 배지를 칠할 성취기준 코드(서버 `matchedStandards`) */
  matchedStandardCodes?: ReadonlySet<string>;
  /**
   * 적용된 필터 조합의 서명. 0건·실패 화면을 **조합이 바뀔 때마다** 다시 마운트시켜
   * 노출을 한 번씩 세게 한다. 같은 조합이면 같은 막다른 길이라 한 건이다.
   */
  resultStateKey?: string;
  /** 0건 화면이 실제로 보였을 때 1회 */
  onEmptyImpression?: () => void;
  /** 실패 화면이 실제로 보였을 때 1회 */
  onErrorImpression?: () => void;
  onToggle: (problem: QuizBankProblem) => void;
  onUnitClick?: (unitName: string) => void;
  onStandardClick?: (standard: QuizBankStandard) => void;
  onRetry?: () => void;
}

/** 로딩 · 0건 · 에러 · 목록이 같은 레이아웃 슬롯에서 교체된다. */
export const QuizBankProblemList = ({
  status,
  problems,
  selectedIds,
  addedIds,
  selectionFull = false,
  query,
  unitHitTermsById,
  matchedStandardCodes,
  resultStateKey,
  onEmptyImpression,
  onErrorImpression,
  onToggle,
  onUnitClick,
  onStandardClick,
  onRetry
}: QuizBankProblemListProps) => {
  if (status === 'error') return <QuizBankErrorState key={resultStateKey} onImpress={onErrorImpression} onRetry={onRetry} />;
  if (status === 'loading') {
    return <div className="flex flex-col gap-md">
        {Array.from({
        length: QUIZ_BANK_SKELETON_COUNT
      }).map((_, index) => <QuizBankProblemCardSkeleton key={index} />)}
      </div>;
  }
  if (problems.length === 0) return <QuizBankEmptyResult key={resultStateKey} query={query} onImpress={onEmptyImpression} />;
  return <ul className="flex list-none flex-col gap-sm p-none" data-sentry-component="QuizBankProblemList" data-sentry-source-file="QuizBankProblemList.tsx">
      {problems.map(problem => <li key={problem.id}>
          <QuizBankProblemCard problem={problem} selected={selectedIds.includes(problem.id)} added={addedIds.includes(problem.id)} selectionDisabled={selectionFull && !selectedIds.includes(problem.id)} unitHitTerms={problem.standard.unitId ? unitHitTermsById?.get(problem.standard.unitId) : undefined} standardHit={matchedStandardCodes?.has(problem.standard.code)} onToggle={onToggle} onUnitClick={onUnitClick} onStandardClick={onStandardClick} />
        </li>)}
    </ul>;
};